import type { PaymentPlan, PaymentPlanDiscount, PrepayPeriodOrContinue, PriceVO } from '@ctct/domain-layer';

import { PayflowPrepayOptionsStrategy } from './PayflowPrepayOptionsStrategy';
import type { TranslationGetter } from '../../../types';
import type { PaymentPlanDiscountDisplayVerbose, PrepayOptionsStrategy } from '../types';

export class ChangePaymentPlanPrepayOptionsStrategy
  extends PayflowPrepayOptionsStrategy
  implements PrepayOptionsStrategy
{
  constructor(opts: { t: TranslationGetter; allowSelectionOfShorterTerm: boolean; isStripe?: boolean }) {
    super({ t: opts.t, allowSelectionOfShorterTerm: opts.allowSelectionOfShorterTerm, isStripe: opts.isStripe });
  }

  public getAvailablePeriodOptions(paymentPlan: PaymentPlan): PaymentPlanDiscount[] {
    return paymentPlan.getChangePaymentPlanDiscounts();
  }

  public getVerbosePeriodDisplayParameters(
    planDiscounts: PaymentPlanDiscount[],
    paymentPlan: PaymentPlan,
    planBaseMonthlyPrice: null | { value: PriceVO; currency: string }
  ): PaymentPlanDiscountDisplayVerbose[] {
    const currentPeriod = paymentPlan.getCurrentPaymentPeriod();
    const isStripe = paymentPlan.getIsStripe();

    return planDiscounts.map(({ discountPeriod, discountPercent }) => {
      const value = String(discountPeriod);
      const disabled = (isStripe || !this.opts.allowSelectionOfShorterTerm) && discountPeriod < currentPeriod;
      const descriptionI18nKey = disabled
        ? 'application-layer.payment-plans.change-plan.disabled-description'
        : `application-layer.payment-plans.change-plan.options.${value}.description`;

      const prepaySavings = planBaseMonthlyPrice
        ? planBaseMonthlyPrice.value
            .multiply((discountPercent / 100) * discountPeriod)
            .format(planBaseMonthlyPrice.currency)
        : null;

      // Use -stripe suffix for Stripe label keys (only periods 6+ have -stripe variants)
      const labelSuffix = this.opts.isStripe && discountPeriod >= 6 ? '-stripe' : '';
      const labelI18nKey = `application-layer.payment-plans.change-plan.options.${value}.label${labelSuffix}`;

      return {
        id: String(discountPeriod),
        labelI18nKey,
        descriptionI18nKey,
        discountPercent,
        savings: prepaySavings,
        isCurrent: currentPeriod === discountPeriod,
        disabled,
      };
    });
  }

  public getPreselectedPeriod(paymentPlan: PaymentPlan) {
    const currentPeriod = paymentPlan.getCurrentPaymentPeriod();
    const isStripe = paymentPlan.getIsStripe();
    const options = paymentPlan.getChangePaymentPlanDiscounts();

    const isEnabled = ({ discountPeriod }: PaymentPlanDiscount) => {
      if (isStripe) {
        return discountPeriod >= currentPeriod;
      }

      const wouldBeDisabled = (isStripe || !this.opts.allowSelectionOfShorterTerm) && discountPeriod < currentPeriod;

      return !wouldBeDisabled;
    };

    if (!isStripe) {
      const currentPeriodOption = options.find(({ discountPeriod }) => discountPeriod === currentPeriod);

      if (currentPeriodOption && isEnabled(currentPeriodOption)) {
        return String(currentPeriod) as PrepayPeriodOrContinue;
      }
    }

    const enabled = options.find(isEnabled);

    return String(
      enabled?.discountPeriod ?? paymentPlan.getPreselectedChangePaymentPlanPeriod()
    ) as PrepayPeriodOrContinue;
  }

  public getIsPrepayPeriodValid(paymentPlan: PaymentPlan, prepayPeriod: string): boolean {
    return paymentPlan
      .getChangePaymentPlanDiscounts()
      .some((discount) => String(discount.discountPeriod) === prepayPeriod);
  }
}
